const { NETWORK_CONFIG } = require('@configs')
const { getMethodId } = require('../../helpers/getMethodId')
const { batchRequest } = require('./batch.hook.service')()
const { ZERO_BYTES, ZERO_ADDRESS } = require('@constants')

/**
 * Vibi blockchain service
 * @returns {Object} Object containing Vibi blockchain operations
 */
const vibiService = () => {
    const getTransactionByDeltaTime = async deltaTime => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=stats&action=txcount`,
                    method: 'POST',
                    data: { deltaTime: deltaTime }
                }
            ]

            const results = await batchRequest(requests)

            const data = results[0].data.result.map(item => Number(item))

            return data
        } catch (error) {
            console.error('Error getting daily transactions:', error)
            return []
        }
    }

    const getGeneralInfo = async () => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=stats&action=generalinfo`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const generalinfo = results[0].data.result

            const data = {
                totalActiveAddresses: Number(generalinfo.totalActiveAddresses),
                latestBlockNumber: Number(generalinfo.latestBlockNumber),
                totalTransactions: Number(generalinfo.totalTransactions)
            }

            return data
        } catch (err) {
            console.error(err)
        }
    }

    // Block operations
    const getBlocks = async (page, limit) => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=blocks&action=getblocks&page=${page}&limit=${limit}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const blocks = results[0].data.result

            const data = {
                items: blocks.blocks.map(block => ({
                    number: Number(block.number),
                    hash: block.hash,
                    mixHash: block.mixHash,
                    parentHash: block.parentHash,
                    nonce: Number(block.nonce),
                    sha3Uncles: block.sha3Uncles,
                    logsBloom: block.logsBloom,
                    transactionsRoot: block.transactionsRoot,
                    stateRoot: block.stateRoot,
                    receiptsRoot: block.receiptsRoot,
                    miner: block.miner == ZERO_ADDRESS ? '0xceb6B88d6cA568D134Ab32D4592F606c3A308684' : block.miner,
                    difficulty: block.difficulty,
                    totalDifficulty: block.totalDifficulty,
                    extraData: block.extraData,
                    size: Number(block.size),
                    gasLimit: Number(block.gasLimit),
                    gasUsed: Number(block.gasUsed),
                    totalTransactionFees: Number(block.totalTransactionFees),
                    timestamp: Number(block.timestamp),
                    transactionCount: Number(block.transactionCount),
                    internalTransactionCount: Number(block.internalTransactionCount),
                    blobTransactionCount: Number(block.blobTransactionCount),
                    blobTransactions: [],
                    baseFeePerGas: Number(block.baseFeePerGas),
                    withdrawalsRoot: block.withdrawalsRoot,
                    withdrawals: Number(block.withdrawals),
                    blobGasUsed: Number(block.blobGasUsed),
                    excessBlobGas: Number(block.excessBlobGas),
                    parentBeaconBlockRoot: block.parentBeaconBlockRoot,

                    beacon: {
                        slot: Number(block.beacon.slot),
                        epoch: Number(block.beacon.epoch),
                        proposerIndex: Number(block.beacon.proposerIndex),
                        blockRandomness: block.beacon.blockRandomness,
                        randaoReveal: block.beacon.randaoReveal,
                        depositRoot: block.beacon.depositRoot,
                        depositCount: Number(block.beacon.depositCount),
                        graffiti: block.beacon.graffiti,
                        proposerSlashings: block.beacon.proposerSlashings,
                        attesterSlashings: block.beacon.attesterSlashings,
                        attestations: block.beacon.attestations,
                        deposits: block.beacon.deposits,
                        voluntaryExits: block.beacon.voluntaryExits
                    }
                })),
                totalItems: Number(blocks.totalItems),
                totalPages: Number(blocks.totalPages),
                page: Number(blocks.page),
                limit: Number(blocks.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting blocks:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getBlock = async blockNumber => {
        try {
            if (blockNumber < 0) return null

            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=block&action=getblockdetails&blockno=${blockNumber}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const block = results[0].data.result

            const data = {
                number: Number(block.number),
                hash: block.hash,
                mixHash: block.mixHash,
                parentHash: block.parentHash,
                nonce: Number(block.nonce),
                sha3Uncles: block.sha3Uncles,
                logsBloom: block.logsBloom,
                transactionsRoot: block.transactionsRoot,
                stateRoot: block.stateRoot,
                receiptsRoot: block.receiptsRoot,
                miner: block.miner == ZERO_ADDRESS ? '0xceb6B88d6cA568D134Ab32D4592F606c3A308684' : block.miner,
                difficulty: block.difficulty,
                totalDifficulty: block.totalDifficulty,
                extraData: block.extraData,
                size: Number(block.size),
                gasLimit: Number(block.gasLimit),
                gasUsed: Number(block.gasUsed),
                totalTransactionFees: Number(block.totalTransactionFees),
                timestamp: Number(block.timestamp),
                transactionCount: Number(block.transactionCount),
                internalTransactionCount: Number(block.internalTransactionCount),
                blobTransactionCount: Number(block.blobTransactionCount),
                blobCount: Number(block.blobCount),
                baseFeePerGas: Number(block.baseFeePerGas),
                withdrawalsRoot: block.withdrawalsRoot,
                withdrawals: Number(block.withdrawals),
                blobGasUsed: Number(block.blobGasUsed),
                blobGasPrice: Number(block.blobGasPrice),
                blobGasLimit: Number(block.blobGasLimit),
                excessBlobGas: Number(block.excessBlobGas),
                parentBeaconBlockRoot: block.parentBeaconBlockRoot,

                beacon: {
                    slot: Number(block.beacon.slot),
                    epoch: Number(block.beacon.epoch),
                    blockRoot: block.beacon.blockRoot,
                    proposerIndex: Number(block.beacon.proposerIndex),
                    blockRandomness: block.beacon.blockRandomness,
                    randaoReveal: block.beacon.randaoReveal,
                    depositRoot: block.beacon.depositRoot,
                    depositCount: Number(block.beacon.depositCount),
                    graffiti: block.beacon.graffiti,
                    proposerSlashings: block.beacon.proposerSlashings,
                    attesterSlashings: block.beacon.attesterSlashings,
                    attestations: block.beacon.attestations,
                    deposits: block.beacon.deposits,
                    voluntaryExits: block.beacon.voluntaryExits
                }
            }

            return data
        } catch (error) {
            console.error('Error getting block:', error)
            return {}
        }
    }

    // Transaction operations
    const getTransactionsStatistics = async () => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=transactions&action=gettransactionsstatistics`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const statistics = results[0].data.result

            const data = {
                totalTransactions: Number(statistics.totalTransactions),
                todayTransactions: Number(statistics.todayTransactions)
            }

            return data
        } catch (error) {
            console.error('Error getting transactions statistics:', error)
            return { totalTransactions: 0, todayTransactions: 0 }
        }
    }

    const getTransactions = async (page, limit, filter) => {
        try {
            const requests = [
                {
                    url:
                        `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=transactions&action=gettransactions&page=${page}&limit=${limit}` +
                        (filter?.blockNumber ? `&blockno=${filter.blockNumber}` : '') +
                        (filter?.address ? `&address=${filter.address}` : ''),
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const transactions = results[0].data.result

            const data = {
                items: transactions.transactions.map(transaction => ({
                    abi: transaction.abi,
                    accessList: transaction.accessList,
                    blobCommitment: [],
                    blobGasPrice: Number(transaction.blobGasPrice),
                    blobGasUsed: Number(transaction.blobGasUsed),
                    blobVersionedHashes: transaction.blobVersionedHashes,
                    blockHash: transaction.blockHash,
                    blockNumber: Number(transaction.blockNumber),
                    blockTimestamp: Number(transaction.blockTimestamp),
                    chainId: Number(NETWORK_CONFIG.vibi.chainId),
                    contractAddress: transaction.contractAddress,
                    cumulativeGasUsed: Number(transaction.cumulativeGasUsed),
                    effectiveGasPrice: Number(transaction.effectiveGasPrice),
                    from: {
                        address: transaction.from.address,
                        type: transaction.from.type
                    },
                    functionName: transaction.functionName,
                    gas: Number(transaction.gas),
                    gasPrice: Number(transaction.gasPrice),
                    gasUsed: Number(transaction.gasUsed),
                    hash: transaction.hash,
                    input: transaction.input,
                    logs: [],
                    logsBloom: ZERO_BYTES,
                    maxFeePerBlobGas: Number(transaction.maxFeePerBlobGas),
                    maxFeePerGas: Number(transaction.maxFeePerGas),
                    maxPriorityFeePerGas: Number(transaction.maxPriorityFeePerGas),
                    nonce: Number(transaction.nonce),
                    status: transaction.status,
                    to: {
                        address: transaction.to.address,
                        type: transaction.to.type
                    },
                    transactionIndex: Number(transaction.transactionIndex),
                    type: Number(transaction.type),
                    value: Number(transaction.value)
                })),

                totalItems: Number(transactions.totalItems),
                totalPages: Number(transactions.totalPages),
                page: Number(transactions.page),
                limit: Number(transactions.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting transactions:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getPendingTransactions = async (page, limit) => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=transactions&action=getpendingtransactions&page=${page}&limit=${limit}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const pendingTransactions = results[0].data.result

            const data = {
                items: pendingTransactions.pendingTransactions.map(transaction => ({
                    abi: transaction.abi,
                    accessList: transaction.accessList,
                    blobCommitment: [],
                    blobGasPrice: Number(transaction.blobGasPrice),
                    blobGasUsed: Number(transaction.blobGasUsed),
                    blobVersionedHashes: transaction.blobVersionedHashes,
                    blockHash: transaction.blockHash,
                    blockNumber: Number(transaction.blockNumber),
                    blockTimestamp: Number(transaction.blockTimestamp),
                    chainId: Number(NETWORK_CONFIG.vibi.chainId),
                    contractAddress: transaction.contractAddress,
                    cumulativeGasUsed: Number(transaction.cumulativeGasUsed),
                    effectiveGasPrice: Number(transaction.effectiveGasPrice),
                    from: {
                        address: transaction.from.address,
                        type: transaction.from.type
                    },
                    functionName: transaction.functionName,
                    gas: Number(transaction.gas),
                    gasPrice: Number(transaction.gasPrice),
                    gasUsed: Number(transaction.gasUsed),
                    hash: transaction.hash,
                    input: transaction.input,
                    logs: [],
                    logsBloom: ZERO_BYTES,
                    maxFeePerBlobGas: Number(transaction.maxFeePerBlobGas),
                    maxFeePerGas: Number(transaction.maxFeePerGas),
                    maxPriorityFeePerGas: Number(transaction.maxPriorityFeePerGas),
                    nonce: Number(transaction.nonce),
                    status: transaction.status,
                    to: {
                        address: transaction.to.address,
                        type: transaction.to.type
                    },
                    transactionIndex: Number(transaction.transactionIndex),
                    type: Number(transaction.type),
                    value: Number(transaction.value)
                })),

                totalItems: Number(pendingTransactions.totalItems),
                totalPages: Number(pendingTransactions.totalPages),
                page: Number(pendingTransactions.page),
                limit: Number(pendingTransactions.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting pendingTransactions:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getTransaction = async transactionHash => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=transaction&action=gettransactiondetails&txhash=${transactionHash}`,
                    method: 'GET'
                },
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=transaction&action=gettransactioninternaltransactions&txhash=${transactionHash}`,
                    method: 'GET'
                },
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=transaction&action=gettransactiontokentransfers&txhash=${transactionHash}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const transactionDetails = results[0].data.result
            const transactionInternalTransactions = results[1].data.result
            const transacionTokenTransfers = results[2].data.result

            const data = {
                details: {
                    abi: transactionDetails.abi,
                    accessList: transactionDetails.accessList,
                    blobCommitments: transactionDetails.blobCommitments,
                    blobGasPrice: Number(transactionDetails.blobGasPrice),
                    blobGasUsed: Number(transactionDetails.blobGasUsed),
                    blobVersionedHashes: transactionDetails.blobVersionedHashes,
                    blockHash: transactionDetails.blockHash,
                    blockNumber: Number(transactionDetails.blockNumber),
                    blockTimestamp: Number(transactionDetails.blockTimestamp),
                    chainId: Number(NETWORK_CONFIG.vibi.chainId),
                    contractAddress: transactionDetails.contractAddress,
                    cumulativeGasUsed: Number(transactionDetails.cumulativeGasUsed),
                    effectiveGasPrice: Number(transactionDetails.effectiveGasPrice),
                    from: {
                        address: transactionDetails.from?.address,
                        type: transactionDetails.from?.type
                    },
                    functionName: transactionDetails.functionName,
                    gas: Number(transactionDetails.gas),
                    gasPrice: Number(transactionDetails.gasPrice),
                    gasUsed: Number(transactionDetails.gasUsed),
                    hash: transactionDetails.hash,
                    input: transactionDetails.input,
                    logs: transactionDetails.logs.map(log => ({
                        abi: log.abi,
                        address: log.address,
                        logIndex: Number(log.logIndex),
                        topics: log.topics.filter(topic => topic !== null),
                        data: log.data
                    })),
                    maxFeePerBlobGas: Number(transactionDetails.maxFeePerBlobGas),
                    maxFeePerGas: Number(transactionDetails.maxFeePerGas),
                    maxPriorityFeePerGas: Number(transactionDetails.maxPriorityFeePerGas),
                    nonce: Number(transactionDetails.nonce),
                    status: transactionDetails.status,
                    to: {
                        address: transactionDetails.to?.address,
                        type: transactionDetails.to?.type
                    },
                    transactionIndex: Number(transactionDetails.transactionIndex),
                    type: Number(transactionDetails.type),
                    value: Number(transactionDetails.value)
                },

                traces: transactionInternalTransactions.map(transaction => ({
                    callType: transaction.call_type,
                    error: transaction.error,
                    from: {
                        address: transaction.from.address,
                        type: transaction.from.type
                    },
                    functionName: transaction.functionName,
                    gas: Number(transaction.gas),
                    gasUsed: Number(transaction.gasUsed),
                    status: transaction.status,
                    to: {
                        address: transaction.to.address,
                        type: transaction.to.type
                    },
                    traceAddress: null,
                    traceType: transaction.traceType,
                    value: transaction.value,
                    rewardType: null,
                    subtraces: null
                })),

                tokenTransfers: transacionTokenTransfers.tokenTransfers.map(transaction => ({
                    from: {
                        address: transaction.from.address,
                        type: transaction.from.type
                    },
                    to: {
                        address: transaction.to.address,
                        type: transaction.to.type
                    },
                    tokenAddress: transaction.tokenAddress,
                    tokenId: Number(transaction.tokenId),
                    tokenValue: Number(transaction.tokenValue),
                    tokenName: transaction.tokenName,
                    tokenSymbol: transaction.tokenSymbol,
                    tokenDecimals: Number(transaction.tokenDecimals),
                    tokenType: transaction.tokenType
                })),

                states: transacionTokenTransfers.states.map(state => ({
                    address: state.address,
                    tokenName: state.tokenName,
                    tokenSymbol: state.tokenSymbol,
                    tokenDecimals: Number(state.tokenDecimals),
                    balanceBefore: Number(state.balanceBefore),
                    balanceAfter: Number(state.balanceAfter)
                }))
            }

            return data
        } catch (error) {
            console.error('Error getting transaction:', error)
            return {}
        }
    }

    //Internal Transaction
    const getInternalTransactions = async (page, limit, filter) => {
        try {
            const requests = [
                {
                    url:
                        `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=internaltransactions&action=getinternaltransactions&page=${page}&limit=${limit}` +
                        (filter?.blockNumber ? `&blockno=${filter.blockNumber}` : '') +
                        (filter?.address ? `&address=${filter.address}` : ''),
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const internalTransactions = results[0].data.result

            const data = {
                items: internalTransactions.blocks.map(block => ({
                    blockHash: block.blockHash,
                    blockNumber: Number(block.blockNumber),
                    blockTimestamp: Number(block.blockTimestamp),
                    transactions: block.internalTransactions.map(transaction => ({
                        callType: transaction.callType,
                        error: transaction.error,
                        from: {
                            address: transaction.from.address,
                            type: transaction.from.type
                        },
                        functionName: transaction.functionName,
                        gas: Number(transaction.gas),
                        gasUsed: Number(transaction.gasUsed),
                        hash: transaction.hash,
                        input: transaction.input,
                        output: transaction.output,
                        status: transaction.status,
                        to: {
                            address: transaction.to.address,
                            type: transaction.to.type
                        },
                        traceAddress: null,
                        traceType: transaction.traceType,
                        transactionIndex: null,
                        value: transaction.value,
                        rewardType: null,
                        subtraces: null
                    }))
                })),

                totalItems: Number(internalTransactions.totalItems),
                totalPages: Number(internalTransactions.totalPages),
                page: Number(internalTransactions.page),
                limit: Number(internalTransactions.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting internal transactions:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getVerifiedContracts = async (page, limit) => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=contracts&action=getverifiedcontracts&page=${page}&limit=${limit}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const contracts = results[0].data.result

            const data = {
                items: contracts.contracts.map(contract => ({
                    address: contract.address,
                    balance: Number(contract.balance),
                    compiler: contract.compiler,
                    constructor: contract.constructor,
                    name: contract.name,
                    optimizationUsed: contract.optimizationUsed,
                    totalTransactions: Number(contract.totalTransactions),
                    verified: contract.verified,
                    version: contract.version,
                    licenseType: contract.licenseType
                })),

                totalItems: Number(contracts.totalItems),
                totalPages: Number(contracts.totalPages),
                page: Number(contracts.page),
                limit: Number(contracts.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting verified contracts:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getSimilarContracts = async (page, limit, address) => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=contracts&action=getsimilarcontracts&page=${page}&limit=${limit}&address=${address}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const contracts = results[0].data.result

            const data = {
                items: contracts.contracts.map(contract => ({
                    address: contract.address,
                    transactionHash: contract.transactionHash
                })),

                totalItems: Number(contracts.totalItems),
                totalPages: Number(contracts.totalPages),
                page: Number(contracts.page),
                limit: Number(contracts.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting verified contracts:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getContractsStatistics = async () => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=contracts&action=getcontractsstatistics`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const statistics = results[0].data.result

            const data = {
                totalDeployed: Number(statistics.totalDeployed),
                todayDeployed: Number(statistics.todayDeployed),
                totalVerified: Number(statistics.totalVerified),
                todayVerified: Number(statistics.todayVerified)
            }

            return data
        } catch (error) {
            console.error('Error getting contract statistics:', error)
            return { totalDeployed: 0, todayDeployed: 0, totalVerified: 0, todayVerified: 0 }
        }
    }

    // Account operations
    const getAddressOverview = async address => {
        try {
            const lowercaseAddress = address.toLowerCase()

            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=account&action=addressoverview&address=${lowercaseAddress}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const overview = results[0].data.result

            const data = {
                balance: Number(overview.balance),
                type: overview.type,
                information: {
                    bytecode: overview.information.bytecode,
                    createTransactionHash: {
                        hash: overview.information.createTransaction?.hash,
                        from: overview.information.createTransaction?.from
                    },
                    contract: {
                        abi: overview.information.contract?.abi ? JSON.parse(overview.information.contract?.abi) : [],
                        compilerVersion: overview.information.contract?.compilerVersion,
                        constructor: overview.information.contract?.constructor,
                        constructorArguments: overview.information.contract?.constructorArguments,
                        creationCode: overview.information.contract?.creationCode,
                        licenseType: overview.information.contract?.licenseType,
                        name: overview.information.contract?.name,
                        optimizationUsed: overview.information.contract?.optimizationUsed,
                        runs: Number(overview.information.contract?.runs),
                        similarContract: overview.information.contract?.similarContract,
                        sourceCode: overview.information.contract?.sourceCode
                    },
                    token: {
                        tokenName: overview.information.token?.tokenName,
                        tokenSymbol: overview.information.token?.tokenSymbol,
                        tokenDecimals: Number(overview.information.token?.tokenDecimals),
                        totalSupply: Number(overview.information.token?.totalSupply),
                        tokenType: overview.information.token?.tokenType
                    },
                    firstTransaction: {
                        hash: overview.information.firstTransaction?.hash,
                        blockTimestamp: Number(overview.information.firstTransaction?.blockTimestamp)
                    },
                    latestTransaction: {
                        hash: overview.information.latestTransaction?.hash,
                        blockTimestamp: Number(overview.information.latestTransaction?.blockTimestamp)
                    }
                }
            }

            return data
        } catch (error) {
            console.error('Error getting address overview:', error)
            return {}
        }
    }

    const getAddress = async address => {
        try {
            const lowercaseAddress = address.toLowerCase()

            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=account&action=addressoverview&address=${lowercaseAddress}`,
                    method: 'GET'
                },
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=internaltransactions&action=getinternaltransactions&page=1&limit=25&address=${lowercaseAddress}`,
                    method: 'GET'
                },
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=account&action=gettokens&address=${lowercaseAddress}`,
                    method: 'GET'
                },
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=tokens&action=gettokentransfers&page=1&limit=25&useraddress=${lowercaseAddress}&tokentype=token`,
                    method: 'GET'
                },
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=tokens&action=gettokentransfers&page=1&limit=25&useraddress=${lowercaseAddress}&tokentype=nft`,
                    method: 'GET'
                },
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=transactions&action=gettransactions&page=1&limit=25&address=${lowercaseAddress}`,
                    method: 'GET'
                },
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=account&action=multichainbalance&address=${lowercaseAddress}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const overview = results[0].data.result
            const internalTransactions = results[1].data.result
            const tokens = results[2].data.result
            const tokenTransfers = results[3].data.result
            const nftTransfers = results[4].data.result
            const transactions = results[5].data.result
            const multichainBalance = results[6].data.result

            const data = {
                balance: Number(overview.balance),
                type: overview.type,
                information: {
                    multichainBalance: {
                        agd: {
                            balance: Number(multichainBalance.agridential.balance),
                            tokens: multichainBalance.agridential.tokens.map(token => ({
                                tokenAddress: token.tokenAddress,
                                tokenName: token.tokenName,
                                tokenSymbol: token.tokenSymbol,
                                tokenDecimals: Number(token.tokenDecimals),
                                totalSupply: Number(token.totalSupply),
                                tokenType: token.tokenType,
                                balance: Number(token.balance)
                            }))
                        },
                        hg: {
                            balance: Number(multichainBalance.haugiang.balance),
                            tokens: multichainBalance.haugiang.tokens.map(token => ({
                                tokenAddress: token.tokenAddress,
                                tokenName: token.tokenName,
                                tokenSymbol: token.tokenSymbol,
                                tokenDecimals: Number(token.tokenDecimals),
                                totalSupply: Number(token.totalSupply),
                                tokenType: token.tokenType,
                                balance: Number(token.balance)
                            }))
                        },
                        vibi: {
                            balance: Number(multichainBalance.vibi.balance),
                            tokens: multichainBalance.vibi.tokens.map(token => ({
                                tokenAddress: token.tokenAddress,
                                tokenName: token.tokenName,
                                tokenSymbol: token.tokenSymbol,
                                tokenDecimals: Number(token.tokenDecimals),
                                totalSupply: Number(token.totalSupply),
                                tokenType: token.tokenType,
                                balance: Number(token.balance)
                            }))
                        },
                        vibi2: {
                            balance: Number(multichainBalance.vibi2.balance),
                            tokens: multichainBalance.vibi2.tokens.map(token => ({
                                tokenAddress: token.tokenAddress,
                                tokenName: token.tokenName,
                                tokenSymbol: token.tokenSymbol,
                                tokenDecimals: Number(token.tokenDecimals),
                                totalSupply: Number(token.totalSupply),
                                tokenType: token.tokenType,
                                balance: Number(token.balance)
                            }))
                        },
                        mbc: {
                            balance: Number(multichainBalance.mbc.balance),
                            tokens: multichainBalance.mbc.tokens.map(token => ({
                                tokenAddress: token.tokenAddress,
                                tokenName: token.tokenName,
                                tokenSymbol: token.tokenSymbol,
                                tokenDecimals: Number(token.tokenDecimals),
                                totalSupply: Number(token.totalSupply),
                                tokenType: token.tokenType,
                                balance: Number(token.balance)
                            }))
                        }
                    },
                    bytecode: overview.information.bytecode,
                    createTransactionHash: {
                        hash: overview.information.createTransaction?.hash,
                        from: overview.information.createTransaction?.from
                    },
                    contract: {
                        abi: overview.information.contract?.abi ? JSON.parse(overview.information.contract?.abi) : [],
                        compilerVersion: overview.information.contract?.compilerVersion,
                        constructor: overview.information.contract?.constructor,
                        constructorArguments: overview.information.contract?.constructorArguments,
                        creationCode: overview.information.contract?.creationCode,
                        licenseType: overview.information.contract?.licenseType,
                        name: overview.information.contract?.name,
                        optimizationUsed: overview.information.contract?.optimizationUsed,
                        runs: Number(overview.information.contract?.runs),
                        similarContract: overview.information.contract?.similarContract,
                        sourceCode: overview.information.contract?.sourceCode
                    },
                    token: {
                        tokenName: overview.information.token?.tokenName,
                        tokenSymbol: overview.information.token?.tokenSymbol,
                        tokenDecimals: Number(overview.information.token?.tokenDecimals),
                        totalSupply: Number(overview.information.token?.totalSupply),
                        tokenType: overview.information.token?.tokenType
                    },
                    firstTransaction: {
                        hash: overview.information.firstTransaction?.hash,
                        blockTimestamp: Number(overview.information.firstTransaction?.blockTimestamp)
                    },
                    latestTransaction: {
                        hash: overview.information.latestTransaction?.hash,
                        blockTimestamp: Number(overview.information.latestTransaction?.blockTimestamp)
                    }
                },
                internalTransactions: {
                    totalItems: internalTransactions.totalItems,
                    items: internalTransactions.blocks.flatMap(block =>
                        block.internalTransactions.map(transaction => ({
                            blockHash: block.blockHash,
                            blockNumber: Number(block.blockNumber),
                            blockTimestamp: Number(block.blockTimestamp),
                            callType: transaction.callType,
                            error: transaction.error,
                            from: {
                                address: transaction.from.address,
                                type: transaction.from.type
                            },
                            functionName: transaction.functionName,
                            gas: Number(transaction.gas),
                            gasUsed: Number(transaction.gasUsed),
                            hash: transaction.hash,
                            input: transaction.input,
                            output: transaction.output,
                            status: transaction.status,
                            to: {
                                address: transaction.to.address,
                                type: transaction.to.type
                            },
                            traceAddress: null,
                            traceType: transaction.traceType,
                            transactionIndex: null,
                            value: transaction.value,
                            rewardType: null,
                            subtraces: null
                        }))
                    )
                },
                tokens: {
                    totalItems: Number(tokens.totalItems),
                    items: tokens.tokens.map(token => ({
                        tokenAddress: token.tokenAddress,
                        tokenName: token.tokenName,
                        tokenSymbol: token.tokenSymbol,
                        tokenDecimals: Number(token.tokenDecimals),
                        totalSupply: Number(token.totalSupply),
                        tokenType: token.tokenType,
                        balance: Number(token.balance)
                    }))
                },
                tokenTransfers: {
                    totalItems: Number(tokenTransfers.totalItems),
                    items: tokenTransfers.transfers.map(transaction => ({
                        accessList: transaction.accessList,
                        blobGasPrice: Number(transaction.blobGasPrice),
                        blobGasUsed: Number(transaction.blobGasUsed),
                        blobVersionedHashes: transaction.blobVersionedHashes,
                        blockHash: transaction.blockHash,
                        blockNumber: Number(transaction.blockNumber),
                        blockTimestamp: Number(transaction.blockTimestamp),
                        chainId: Number(NETWORK_CONFIG.vibi.chainId),
                        contractAddress: transaction.contractAddress,
                        cumulativeGasUsed: Number(transaction.cumulativeGasUsed),
                        effectiveGasPrice: Number(transaction.effectiveGasPrice),
                        from: {
                            address: transaction.from?.address,
                            type: transaction.from?.type
                        },
                        functionName: transaction.functionName,
                        gas: Number(transaction.gas),
                        gasPrice: Number(transaction.gasPrice),
                        gasUsed: Number(transaction.gasUsed),
                        hash: transaction.hash,
                        input: transaction.input,
                        logs: transaction.logs,
                        maxFeePerBlobGas: Number(transaction.maxFeePerBlobGas),
                        maxFeePerGas: Number(transaction.maxFeePerGas),
                        maxPriorityFeePerGas: Number(transaction.maxPriorityFeePerGas),
                        nonce: Number(transaction.nonce),
                        status: transaction.status,
                        to: {
                            address: transaction.to?.address,
                            type: transaction.to?.type
                        },
                        transactionIndex: Number(transaction.transactionIndex),
                        type: Number(transaction.type),
                        value: Number(transaction.value),

                        transferFrom: {
                            address: transaction.transferFrom.address,
                            type: transaction.transferFrom.type
                        },
                        transferTo: {
                            address: transaction.transferTo.address,
                            type: transaction.transferTo.type
                        },
                        tokenAddress: transaction.tokenAddress,
                        tokenName: transaction.tokenName,
                        tokenSymbol: transaction.tokenSymbol,
                        tokenDecimals: Number(transaction.tokenDecimals),
                        tokenValue: Number(transaction.tokenValue)
                    }))
                },
                nftTransfers: {
                    totalItems: Number(nftTransfers.totalItems),
                    items: nftTransfers.transfers.map(transaction => ({
                        accessList: transaction.accessList,
                        blobGasPrice: Number(transaction.blobGasPrice),
                        blobGasUsed: Number(transaction.blobGasUsed),
                        blobVersionedHashes: transaction.blobVersionedHashes,
                        blockHash: transaction.blockHash,
                        blockNumber: Number(transaction.blockNumber),
                        blockTimestamp: Number(transaction.blockTimestamp),
                        chainId: Number(NETWORK_CONFIG.vibi.chainId),
                        contractAddress: transaction.contractAddress,
                        cumulativeGasUsed: Number(transaction.cumulativeGasUsed),
                        effectiveGasPrice: Number(transaction.effectiveGasPrice),
                        from: {
                            address: transaction.from?.address,
                            type: transaction.from?.type
                        },
                        functionName: transaction.functionName,
                        gas: Number(transaction.gas),
                        gasPrice: Number(transaction.gasPrice),
                        gasUsed: Number(transaction.gasUsed),
                        hash: transaction.hash,
                        input: transaction.input,
                        logs: transaction.logs,
                        maxFeePerBlobGas: Number(transaction.maxFeePerBlobGas),
                        maxFeePerGas: Number(transaction.maxFeePerGas),
                        maxPriorityFeePerGas: Number(transaction.maxPriorityFeePerGas),
                        nonce: Number(transaction.nonce),
                        status: transaction.status,
                        to: {
                            address: transaction.to?.address,
                            type: transaction.to?.type
                        },
                        transactionIndex: Number(transaction.transactionIndex),
                        type: Number(transaction.type),
                        value: Number(transaction.value),

                        transferFrom: {
                            address: transaction.transferFrom.address,
                            type: transaction.transferFrom.type
                        },
                        transferTo: {
                            address: transaction.transferTo.address,
                            type: transaction.transferTo.type
                        },
                        tokenAddress: transaction.tokenAddress,
                        tokenName: transaction.tokenName,
                        tokenSymbol: transaction.tokenSymbol,
                        tokenDecimals: Number(transaction.tokenDecimals),
                        tokenId: Number(transaction.tokenId),
                        tokenValue: Number(transaction.tokenValue),
                        tokenType: transaction.tokenType,
                        tokenImage: transaction.tokenImage
                    }))
                },
                transactions: {
                    totalItems: Number(transactions.totalItems),
                    items: transactions.transactions.map(transaction => ({
                        accessList: transaction.accessList,
                        blobGasPrice: Number(transaction.blobGasPrice),
                        blobGasUsed: Number(transaction.blobGasUsed),
                        blobVersionedHashes: transaction.blobVersionedHashes,
                        blockHash: transaction.blockHash,
                        blockNumber: Number(transaction.blockNumber),
                        blockTimestamp: Number(transaction.blockTimestamp),
                        chainId: Number(NETWORK_CONFIG.vibi.chainId),
                        contractAddress: transaction.contractAddress,
                        cumulativeGasUsed: Number(transaction.cumulativeGasUsed),
                        effectiveGasPrice: Number(transaction.effectiveGasPrice),
                        from: {
                            address: transaction.from?.address,
                            type: transaction.from?.type
                        },
                        functionName: transaction.functionName,
                        gas: Number(transaction.gas),
                        gasPrice: Number(transaction.gasPrice),
                        gasUsed: Number(transaction.gasUsed),
                        hash: transaction.hash,
                        input: transaction.input,
                        logs: transaction.logs,
                        maxFeePerBlobGas: Number(transaction.maxFeePerBlobGas),
                        maxFeePerGas: Number(transaction.maxFeePerGas),
                        maxPriorityFeePerGas: Number(transaction.maxPriorityFeePerGas),
                        nonce: Number(transaction.nonce),
                        status: transaction.status,
                        to: {
                            address: transaction.to?.address,
                            type: transaction.to?.type
                        },
                        transactionIndex: Number(transaction.transactionIndex),
                        type: Number(transaction.type),
                        value: Number(transaction.value)
                    }))
                }
            }

            return data
        } catch (error) {
            console.error('Error getting address:', error)
            return {}
        }
    }

    const getTokens = async (page = 1, limit = 25) => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=tokens&action=gettokens&page=${page}&limit=${limit}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const tokens = results[0].data.result

            const data = {
                items: tokens.tokens.map(token => ({
                    tokenAddress: token.tokenAddress,
                    tokenName: token.tokenName,
                    tokenSymbol: token.tokenSymbol,
                    tokenDecimals: Number(token.tokenDecimals),
                    totalSupply: Number(token.totalSupply),
                    tokenType: token.tokenType,
                    totalHolders: Number(token.totalHolders),
                    totalTransfers: Number(token.totalTransfers)
                })),

                totalItems: Number(tokens.totalItems),
                totalPages: Number(tokens.totalPages),
                page: Number(tokens.page),
                limit: Number(tokens.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting tokens:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getTokenOverview = async address => {
        try {
            const lowercaseAddress = address.toLowerCase()

            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=token&action=tokenoverview&address=${lowercaseAddress}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const overview = results[0].data.result

            const data = {
                tokenName: overview.tokenName,
                tokenSymbol: overview.tokenSymbol,
                tokenDecimals: Number(overview.tokenDecimals),
                totalSupply: Number(overview.totalSupply),
                tokenType: overview.tokenType,
                totalHolders: Number(overview.totalHolders),
                totalTransfers: Number(overview.totalTransfers),

                metadata: {
                    blueCheckmark: overview.metadata.blueCheckmark,
                    description: overview.metadata.description,
                    website: overview.metadata.website,
                    email: overview.metadata.email,
                    blog: overview.metadata.blog,
                    reddit: overview.metadata.reddit,
                    slack: overview.metadata.slack,
                    facebook: overview.metadata.facebook,
                    twitter: overview.metadata.twitter,
                    bitcointalk: overview.metadata.bitcointalk,
                    github: overview.metadata.github,
                    telegram: overview.metadata.telegram,
                    wechat: overview.metadata.wechat,
                    linkedin: overview.metadata.linkedin,
                    discord: overview.metadata.discord,
                    whitepaper: overview.metadata.whitepaper
                }
            }

            return data
        } catch (error) {
            console.error('Error getting token:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getTokenTransfers = async (page = 1, limit = 25, filter) => {
        try {
            const requests = [
                {
                    url:
                        `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=tokens&action=gettokentransfers&page=${page}&limit=${limit}` +
                        (filter?.address ? `&address=${filter.address.toLowerCase()}` : '') +
                        (filter?.tokenType ? `&tokentype=${filter.tokenType}` : '') +
                        (filter?.userAddress ? `&useraddress=${filter.userAddress.toLowerCase()}` : ''),
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const transfers = results[0].data.result

            const data = {
                items: transfers.transfers.map(transaction => ({
                    accessList: transaction.accessList,
                    blobGasPrice: Number(transaction.blobGasPrice),
                    blobGasUsed: Number(transaction.blobGasUsed),
                    blobVersionedHashes: transaction.blobVersionedHashes,
                    blockHash: transaction.blockHash,
                    blockNumber: Number(transaction.blockNumber),
                    blockTimestamp: Number(transaction.blockTimestamp),
                    chainId: Number(NETWORK_CONFIG.vibi.chainId),
                    contractAddress: transaction.contractAddress,
                    cumulativeGasUsed: Number(transaction.cumulativeGasUsed),
                    effectiveGasPrice: Number(transaction.effectiveGasPrice),
                    from: {
                        address: transaction.from?.address,
                        type: transaction.from?.type
                    },
                    functionName: transaction.functionName,
                    gas: Number(transaction.gas),
                    gasPrice: Number(transaction.gasPrice),
                    gasUsed: Number(transaction.gasUsed),
                    hash: transaction.hash,
                    input: transaction.input,
                    logs: transaction.logs,
                    maxFeePerBlobGas: Number(transaction.maxFeePerBlobGas),
                    maxFeePerGas: Number(transaction.maxFeePerGas),
                    maxPriorityFeePerGas: Number(transaction.maxPriorityFeePerGas),
                    nonce: Number(transaction.nonce),
                    status: transaction.status,
                    to: {
                        address: transaction.to?.address,
                        type: transaction.to?.type
                    },
                    transactionIndex: Number(transaction.transactionIndex),
                    type: Number(transaction.type),
                    value: Number(transaction.value),

                    transferFrom: {
                        address: transaction.transferFrom.address,
                        type: transaction.transferFrom.type
                    },
                    transferTo: {
                        address: transaction.transferTo.address,
                        type: transaction.transferTo.type
                    },
                    tokenAddress: transaction.tokenAddress,
                    tokenName: transaction.tokenName,
                    tokenSymbol: transaction.tokenSymbol,
                    tokenDecimals: Number(transaction.tokenDecimals),
                    tokenType: transaction.tokenType,
                    tokenId: Number(transaction.tokenId),
                    tokenValue: Number(transaction.tokenValue),
                    tokenImage: transaction.tokenImage
                })),

                totalItems: Number(transfers.totalItems),
                totalPages: Number(transfers.totalPages),
                page: Number(transfers.page),
                limit: Number(transfers.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting token:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getTokenHolders = async (address, page = 1, limit = 25) => {
        try {
            const lowercaseAddress = address.toLowerCase()

            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=token&action=tokenholders&page=${page}&limit=${limit}&address=${lowercaseAddress}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const holders = results[0].data.result

            const data = {
                items: holders.holders.map(holder => ({
                    address: holder.address,
                    type: holder.type,
                    balance: Number(holder.balance)
                })),

                totalItems: Number(holders.totalItems),
                totalPages: Number(holders.totalPages),
                page: Number(holders.page),
                limit: Number(holders.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting token:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getTokenInventory = async (page = 1, limit = 25, filter) => {
        try {
            const requests = [
                {
                    url:
                        `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=token&action=tokeninventory&page=${page}&limit=${limit}` +
                        (filter?.address ? `&address=${filter.address.toLowerCase()}` : '') +
                        (filter?.userAddress ? `&useraddress=${filter.userAddress.toLowerCase()}` : ''),
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const inventory = results[0].data.result

            const data = {
                items: inventory.inventory.map(owner => ({
                    tokenId: owner.tokenId,
                    owner: owner.owner
                })),

                totalItems: Number(inventory.totalItems),
                totalPages: Number(inventory.totalPages),
                page: Number(inventory.page),
                limit: Number(inventory.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting token:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getNfts = async (page = 1, limit = 25) => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=tokens&action=getnfts&page=${page}&limit=${limit}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const nfts = results[0].data.result

            const data = {
                items: nfts.nfts.map(nft => ({
                    tokenAddress: nft.tokenAddress,
                    tokenName: nft.tokenName,
                    tokenSymbol: nft.tokenSymbol,
                    tokenDecimals: Number(nft.tokenDecimals),
                    totalSupply: Number(nft.totalSupply),
                    tokenType: nft.tokenType,
                    totalHolders: Number(nft.totalHolders),
                    totalTransfers: Number(nft.totalTransfers)
                })),

                totalItems: Number(nfts.totalItems),
                totalPages: Number(nfts.totalPages),
                page: Number(nfts.page),
                limit: Number(nfts.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting tokens:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getNftsLatestMints = async (page = 1, limit = 25) => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=tokens&action=getnftlatestmints&page=${page}&limit=${limit}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const transfers = results[0].data.result

            const data = {
                items: transfers.transfers.map(transaction => ({
                    accessList: transaction.accessList,
                    blobGasPrice: Number(transaction.blobGasPrice),
                    blobGasUsed: Number(transaction.blobGasUsed),
                    blobVersionedHashes: transaction.blobVersionedHashes,
                    blockHash: transaction.blockHash,
                    blockNumber: Number(transaction.blockNumber),
                    blockTimestamp: Number(transaction.blockTimestamp),
                    chainId: Number(NETWORK_CONFIG.vibi.chainId),
                    contractAddress: transaction.contractAddress,
                    cumulativeGasUsed: Number(transaction.cumulativeGasUsed),
                    effectiveGasPrice: Number(transaction.effectiveGasPrice),
                    from: {
                        address: transaction.from?.address,
                        type: transaction.from?.type
                    },
                    functionName: transaction.functionName,
                    gas: Number(transaction.gas),
                    gasPrice: Number(transaction.gasPrice),
                    gasUsed: Number(transaction.gasUsed),
                    hash: transaction.hash,
                    input: transaction.input,
                    logs: transaction.logs,
                    maxFeePerBlobGas: Number(transaction.maxFeePerBlobGas),
                    maxFeePerGas: Number(transaction.maxFeePerGas),
                    maxPriorityFeePerGas: Number(transaction.maxPriorityFeePerGas),
                    nonce: Number(transaction.nonce),
                    status: transaction.status,
                    to: {
                        address: transaction.to?.address,
                        type: transaction.to?.type
                    },
                    transactionIndex: Number(transaction.transactionIndex),
                    type: Number(transaction.type),
                    value: Number(transaction.value),

                    transferFrom: {
                        address: transaction.transferFrom.address,
                        type: transaction.transferFrom.type
                    },
                    transferTo: {
                        address: transaction.transferTo.address,
                        type: transaction.transferTo.type
                    },
                    tokenAddress: transaction.tokenAddress,
                    tokenName: transaction.tokenName,
                    tokenSymbol: transaction.tokenSymbol,
                    tokenDecimals: Number(transaction.tokenDecimals),
                    tokenType: transaction.tokenType,
                    tokenId: Number(transaction.tokenId),
                    tokenValue: Number(transaction.tokenValue)
                })),

                totalItems: Number(transfers.totalItems),
                totalPages: Number(transfers.totalPages),
                page: Number(transfers.page),
                limit: Number(transfers.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting token:', error)
            return { items: [], totalItems: 0, totalPages: 0, page: 0, limit: 0 }
        }
    }

    const getNftOverview = async (address, tokenId) => {
        try {
            const lowercaseAddress = address.toLowerCase()

            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=token&action=nftoverview&address=${lowercaseAddress}&tokenid=${tokenId}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const overview = results[0].data.result

            const data = {
                tokenName: overview.tokenName,
                tokenSymbol: overview.tokenSymbol,
                tokenType: overview.tokenType,
                tokenCreator: {
                    address: overview.tokenCreator.address
                },
                tokenOwner: {
                    address: overview.tokenOwner.address
                },
                tokenImage: overview.tokenImage,
                metadata: {
                    description: overview.metadata.description,
                    uri: overview.metadata.uri
                },
                properties: overview.properties
            }

            return data
        } catch (error) {
            console.error('Error getting nft:', error)
            return {}
        }
    }

    const verifyContract = async (
        contractAddress,
        sourceCode,
        codeFormat,
        contractName,
        compilerVersion,
        optimization,
        optimizationRuns,
        evmVersion,
        licenseType
    ) => {
        try {
            const lowercaseAddress = contractAddress.toLowerCase()

            const params = new URLSearchParams()
            params.append('contractaddress', lowercaseAddress)
            params.append('sourceCode', sourceCode)
            params.append('codeformat', codeFormat)
            params.append('contractname', contractName)
            params.append('compilerversion', compilerVersion)
            params.append('optimizationUsed', Number(optimization))
            params.append('runs', optimizationRuns)
            params.append('licenseType', licenseType)

            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=contract&action=verifysourcecode`,
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    data: params.toString()
                }
            ]

            const results = await batchRequest(requests)

            const verificationId = results[0].data.result

            const data = {
                verificationId: verificationId
            }

            return data
        } catch (error) {
            console.error('Error verifying contract:', error)
            return {}
        }
    }

    const getVerificationStatus = async verificationId => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=contract&action=checkverifystatus&guid=${verificationId}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const verificationStatus = JSON.parse(results[0].data.result)

            const data = {
                title: verificationStatus.title,
                details: verificationStatus.details
            }

            return data
        } catch (error) {
            console.error('Error getting verification status:', error)
            return {}
        }
    }

    const getConstructor = async address => {
        try {
            const requests = [
                {
                    url: `${
                        NETWORK_CONFIG.vibi.chainApiEndpoint
                    }?module=contract&action=getconstructor&address=${address.toLowerCase()}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)

            const constructor = results[0].data.result

            const data = {
                constructor: constructor.constructor
            }

            return data
        } catch (error) {
            console.error('Error getting contract constructor:', error)
            return {}
        }
    }

    const getApplications = async address => {
        try {
            const requests = [
                {
                    url: `${
                        NETWORK_CONFIG.vibi.chainApiEndpoint
                    }?module=applications&action=getapplicationstatus&address=${address.toLowerCase()}&network=vibi`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)
            const result = results[0].data.result

            const data = {
                type: result.type,
                subgraph: result.subgraph
            }

            return data
        } catch (error) {
            console.error('Error getting application:', error)
            return {}
        }
    }

    const getLogsOpinionCollection = async (page, limit, subgraph, tokenId) => {
        try {
            const requests = [
                {
                    url: `${NETWORK_CONFIG.vibi.chainApiEndpoint}?module=opinionCollection&action=getLogs&page=${page}&limit=${limit}&subgraph=${subgraph}&tokenId=${tokenId}`,
                    method: 'GET'
                }
            ]

            const results = await batchRequest(requests)
            const logs = results[0].data.result

            const data = {
                items: logs.logs,
                totalItems: Number(logs.totalItems),
                totalPages: Number(logs.totalPages),
                page: Number(logs.page),
                limit: Number(logs.limit)
            }

            return data
        } catch (error) {
            console.error('Error getting application:', error)
            return {}
        }
    }

    return {
        getTransactionByDeltaTime,
        getGeneralInfo,
        getBlocks,
        getBlock,
        getTransactionsStatistics,
        getTransactions,
        getPendingTransactions,
        getTransaction,
        getInternalTransactions,
        getVerifiedContracts,
        getSimilarContracts,
        getContractsStatistics,
        getAddressOverview,
        getAddress,
        getTokens,
        getTokenOverview,
        getTokenTransfers,
        getTokenHolders,
        getTokenInventory,
        getNfts,
        getNftsLatestMints,
        getNftOverview,
        verifyContract,
        getVerificationStatus,
        getConstructor,
        getApplications,
        getLogsOpinionCollection
    }
}

module.exports = vibiService
